// Thin fetch client for the admin API (same origin, /admin/api). All calls carry
// the console identity headers supplied by the auth seam (see src/auth). Today
// those are the dev stub headers; after cutover they become a real bearer token
// injected by @solya/auth-verify — this client does not care which.

const BASE = "/admin/api"

/** A structured API error carrying the HTTP status + machine error code. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message?: string) {
    super(message ?? code)
    this.name = "ApiError"
    this.status = status
    this.code = code
  }

  /** True when the write was refused because the console is dark (pre-cutover). */
  get isWritesDisabled(): boolean {
    return this.status === 503 && this.code === "writes_disabled"
  }
}

/** Identity headers injected on every request (the auth seam owns their values). */
export type IdentityHeaders = Record<string, string>

let identityHeaders: IdentityHeaders = {}

/** Set the headers the auth seam wants attached to every request. */
export function setIdentityHeaders(headers: IdentityHeaders): void {
  identityHeaders = headers
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...identityHeaders
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  })

  if (!res.ok) {
    let code = `http_${res.status}`
    try {
      const json = (await res.json()) as { error?: string; detail?: unknown }
      if (typeof json.error === "string") code = json.error
    } catch {
      // Non-JSON error body — keep the generic code.
    }
    throw new ApiError(res.status, code)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body)
}
